import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import toast from "react-hot-toast";
import Select from "react-select";
import "../../styles/walletTransfer.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { AuthContext } from "../../context/AuthContext";
import { selectCustomStyles } from "../../utils/utils";
import WalletTransferServices from "../../services/WalletTransferServices";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import AdminUsersServices from "../../services/AdminUsersServices";
import OutletUsersServices from "../../services/OutletUsersServices";
import MobileUsersServices from "../../services/MobileUsersServices";

const WalletTransfer = () => {
  const walletTransferApiRef = useRef(null);
  if (!walletTransferApiRef.current) {
    walletTransferApiRef.current = new WalletTransferServices();
  }
  const txApi = new TransactionHistoryServices();
  const adminSerApi = new AdminUsersServices();
  const outletSerApi = new OutletUsersServices();
  const mobileSerApi = new MobileUsersServices();

  const { user } = useContext(AuthContext);
  const [from, setFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));

  const [walletPayment, setWalletPayment] = useState("Receipt");
  const [amount, setAmount] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownUsers, setDropdownUsers] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [balanceLog, setBalanceLog] = useState([]);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [q, setQ] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [adminWalletBalance, setAdminWalletBalance] = useState(0.0);

  const userOptions = useMemo(() => {
    const selfId = Number(user?.user_id);
    return (dropdownUsers || [])
      .filter((u) => Number(u.id) !== selfId)
      .map((u) => {
        const id = Number(u.id);
        const uname = u.username || "—";
        return {
          value: id,
          label: `${uname}`,
          raw: u,
        };
      });
  }, [dropdownUsers, user?.user_id]);

  const onAmountChange = (e) => {
    const value = e.target.value;
    const filter = value.replace(/[^0-9]/g, "");
    if (filter !== value) {
      toast.error("Please enter a valid amount in numbers");
      setAmount("");
      return;
    }
    setAmount(filter);
  };

  const onFrom = (e) => {
    setFrom(e.target.value);
  };

  const onTo = (e) => {
    setTo(e.target.value);
  };

  const walletSelectStyles = useMemo(
    () => ({
      ...selectCustomStyles,
      control: (base, state) => ({
        ...(typeof selectCustomStyles.control === "function"
          ? selectCustomStyles.control(base, state)
          : base),
        minWidth: "min(100%, 320px)",
        maxWidth: "100%",
        borderRadius: "10px",
        borderColor: "#fff",
        minHeight: "42px",
      }),
    }),
    [],
  );

  const loadBalanceLog = async () => {
    setLoading(true);
    try {
      const res = await txApi.getBalanceLogHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setBalanceLog(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load balance log");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load balance log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalanceLog();
  }, [page, limit, q, from, to, userId, paymentType]);

  const getDropdownUsers = useCallback(async () => {
    try {
      const res = await walletTransferApiRef.current.getDropdownUsers();
      if (res.isSuccess) {
        setDropdownUsers(res.data || []);
      } else {
        toast.error(res.message || "Failed to load users");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load users");
    }
  }, []);

  const getAdminWalletBalance = useCallback(async () => {
    try {
      const response = await adminSerApi.addAdminWalletBalanceById();

      if (response.isSuccess) {
        setAdminWalletBalance(response?.data?.wallet_balance);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to get admin wallet balance");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    getDropdownUsers();
    if (user?.role_id !== 1) {
      getAdminWalletBalance();
    }
  }, [getDropdownUsers]);

  const handleTransfer = async () => {
    if (!selectedUser?.value) {
      toast.error("Select a user to transfer balance");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    if (
      user.role_id !== 1 &&
      walletPayment === "Receipt" &&
      Number(adminWalletBalance) <= amt
    ) {
      toast.error(
        `Please enter amount less than or equal to ${user?.username} wallet balance.`,
      );
      return;
    } else if (
      walletPayment === "Payment" &&
      Number(selectedUser?.raw?.wallet_balance) <= amt
    ) {
      toast.error(
        `Please enter amount less than or equal to ${selectedUser?.raw?.username} wallet balance.`,
      );
      return;
    }

    let finalAmount = 0;
    let type = "CREDIT";
    let remark = "";

    if (walletPayment === "Payment") {
      type = "DEBIT";
      remark = `Deduct Wallet Balance by ${user?.username}.`;
      if (user.role_id !== 1) {
        finalAmount = (Number(user?.wallet_balance) || 0) + (Number(amt) || 0);
      }
    } else {
      if (user.role_id !== 1) {
        finalAmount = (Number(user?.wallet_balance) || 0) - (Number(amt) || 0);
      }
      remark = `Add Wallet Balance by ${user?.username}.`;
    }

    const payload = {
      user_id: selectedUser?.raw?.id,
      wallet_payment: walletPayment,
      wallet_balance: amt,
      type: type,
      remark: remark,
      admin_wallet_balance: finalAmount,
    };

    setTransferLoading(true);
    try {
      const res =
        selectedUser?.raw?.role_id === 2
          ? await adminSerApi.addAdminWalletBalanceBySA(payload)
          : selectedUser?.raw?.role_id === 3
            ? await outletSerApi.addOutletWalletBalanceBySA(payload)
            : selectedUser?.raw?.role_id === 4
              ? await mobileSerApi.addMobileWalletBalanceBySA(payload)
              : null;
      if (res?.isSuccess) {
        toast.success(res.message || "Transfer completed");
        setAmount("");
        setWalletPayment("Receipt");
        loadBalanceLog();
        await getDropdownUsers();
        setSelectedUser((prev) => ({
          ...prev,
          raw: {
            ...prev?.raw,
            wallet_balance: res.balance,
          },
        }));
        setPage(1);
        if (user.role_id !== 1) {
            // setAdminWalletBalance(finalAmount);
            getAdminWalletBalance();
        }
      } else {
        toast.error(res?.message || "Transfer failed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Transfer failed";
      toast.error(msg);
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div>
      <div className="wallet-transfer-header">
        <div>
          <div className="wallet-transfer-search">
            <Select
              inputId="wallet-transfer-user-select"
              instanceId="wallet-transfer-user"
              className="wallet-transfer-user-select"
              classNamePrefix="wt-select"
              styles={walletSelectStyles}
              options={userOptions}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setPage(1);
                setUserId(opt?.value ?? null);
                setBalanceLog([]);
                setPagination(null);
                setQ("");
                setPaymentType("");
                setAmount("");
                setWalletPayment("Receipt");
              }}
              placeholder="Search by name, username, or ID…"
              isClearable
              isSearchable
              filterOption={(candidate, input) => {
                if (!input) return true;
                const q = input.toLowerCase().trim();
                const r = candidate.data?.raw;
                if (!r)
                  return String(candidate.label).toLowerCase().includes(q);
                const hay = [r.name, r.username, String(r.id)]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return hay.includes(q);
              }}
              noOptionsMessage={() => "No users match"}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
            />
          </div>
          {selectedUser && (
            <div className="wallet-transfer-balance-container">
              <label>
                Balance:{" "}
                <span className="wallet-transfer-balance-amount">
                  {walletPayment === "Receipt"
                    ? Number(selectedUser?.raw?.wallet_balance) +
                      Number(amount || 0)
                    : Number(selectedUser?.raw?.wallet_balance) -
                      Number(amount || 0)}
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="wallet-transfer-filters-container">
          <div className="wallet-transfer-filters">
            <div className="wallet-transfer-date">
              <label>From</label>
              <input type="date" value={from} onChange={onFrom} />
            </div>
            <div className="wallet-transfer-date">
              <label>To</label>
              <input type="date" value={to} onChange={onTo} />
            </div>
          </div>
          {selectedUser && (
            <>
              <div className="wallet-transfer-amount-container">
                <div className="input-wrapper">
                  <input
                    type="radio"
                    className="form-check-input me-1"
                    name="wallet_payment"
                    id="wallet_receipt"
                    value="Receipt"
                    checked={walletPayment === "Receipt"}
                    onChange={(e) => setWalletPayment(e.target.value)}
                  />
                  <label htmlFor="wallet_receipt">Receipt (Add Balance)</label>
                  <input
                    type="radio"
                    className="form-check-input ms-3 me-1"
                    name="wallet_payment"
                    id="wallet_payment"
                    value="Payment"
                    checked={walletPayment === "Payment"}
                    onChange={(e) => setWalletPayment(e.target.value)}
                  />
                  <label htmlFor="wallet_payment">
                    Payment (Deduct Balance)
                  </label>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Enter amount"
                    className="wallet-transfer-amount"
                    value={amount}
                    onChange={onAmountChange}
                  />
                </div>
                {user.role_id !== 1 && (
                  <div>
                    {user.username} balance:
                    <span className="wallet-transfer-balance-amount">
                      {" "}
                      {walletPayment === "Receipt"
                        ? Number(adminWalletBalance) - Number(amount || 0)
                        : Number(adminWalletBalance) + Number(amount || 0)}
                    </span>
                  </div>
                )}
              </div>

              <div className="wallet-transfer-button-container">
                <button
                  type="button"
                  className="wallet-transfer-button"
                  onClick={handleTransfer}
                  disabled={transferLoading}
                >
                  {walletPayment === "Receipt"
                    ? transferLoading
                      ? "Transferring…"
                      : "Transfer"
                    : transferLoading
                      ? "Deducting…"
                      : "Deduct"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {!selectedUser ? (
        <NoData
          message="No User Selected"
          subText="Select a user to transfer to."
        />
      ) : (
        <>
          <div>
            {loading ? (
              <Loader message="Loading balance log..." />
            ) : balanceLog.length === 0 ? (
              <NoData
                message="No balance log found"
                subText="Try changing date range or select another user."
              />
            ) : (
              <>
                <div className="tx-table-bpl-scroll">
                  <div className="tx-table-header-blog">
                    <div>Date</div>
                    <div>Reference</div>
                    <div>Type</div>
                    <div>Amount</div>
                    <div>Balance</div>
                    <div style={{ minWidth: "210px" }}>Remark</div>
                  </div>

                  <div className="tx-table-blog">
                    {balanceLog.map((r) => {
                      const isCredit =
                        String(r.txn_type).toUpperCase() === "CREDIT";
                      return (
                        <div className="tx-row-blog" key={r.id}>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {moment
                                .utc(r.created_at)
                                .local()
                                .format("DD-MMM-YYYY HH:mm A")}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {(() => {
                                const ref = r?.reference_id;
                                if (ref == null || ref === "") return "—";
                                const s = String(ref);
                                return s.length > 13
                                  ? `${s.slice(0, 13)}...`
                                  : s;
                              })()}
                            </div>
                            <div className="tx-muted">
                              {r.reference_type || "-"}
                            </div>
                          </div>
                          <div style={{ fontWeight: 700 }}>{r.txn_type}</div>
                          <div
                            className={`tx-amount ${isCredit ? "credit" : "debit"}`}
                          >
                            {isCredit ? "+" : "-"}
                            {r.amount}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {r.balance_after}
                            </div>
                            <div className="tx-muted">
                              Before: {r.balance_before}
                            </div>
                          </div>
                          <div style={{ minWidth: "210px" }}>
                            {r.remark || "-"}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {pagination && pagination.totalPages > 1 && (
                    <div className="modern-pagination">
                      {/* Limit Selector */}
                      <div className="mp-limit">
                        <span>Show</span>
                        <select
                          value={limit}
                          onChange={(e) => setLimit(e.target.value)}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="200">200</option>
                          <option value="500">500</option>
                          <option value="1000">1000</option>
                        </select>
                      </div>

                      {/* Navigation */}
                      <div className="mp-controls">
                        <button
                          disabled={page === 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Prev
                        </button>

                        <div className="mp-info">
                          Page <strong>{pagination.page}</strong> of{" "}
                          <strong>{pagination.totalPages}</strong>
                        </div>

                        <button
                          disabled={page === pagination.totalPages}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </div>

                      {/* Jump to Page */}
                      <div className="mp-jump">
                        <select
                          value={page}
                          onChange={(e) => setPage(e.target.value)}
                        >
                          {Array.from(
                            { length: pagination.totalPages },
                            (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                Page {i + 1}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WalletTransfer;
